import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Download, Calendar, Mail, Target, Shield, TrendingUp, CheckCircle, Zap, Heart, Activity, AlertTriangle, ArrowRight, Send, User, Building, DollarSign, RefreshCw, ShieldCheck } from 'lucide-react';
import TeamSection from '../components/TeamSection';
import InvestmentSection from '../components/InvestmentSection';
import FAQSection from '../components/FAQSection';

// Simple math captcha generator
const generateCaptcha = () => {
  const num1 = Math.floor(Math.random() * 10) + 1;
  const num2 = Math.floor(Math.random() * 10) + 1;
  const operators = ['+', '-', '×'];
  const operator = operators[Math.floor(Math.random() * 3)];
  
  let answer;
  switch (operator) {
    case '+':
      answer = num1 + num2;
      break;
    case '-':
      answer = Math.max(num1, num2) - Math.min(num1, num2);
      return { 
        question: `${Math.max(num1, num2)} ${operator} ${Math.min(num1, num2)}`, 
        answer 
      };
    case '×':
      answer = num1 * num2;
      break;
    default:
      answer = num1 + num2;
  }
  
  return { question: `${num1} ${operator} ${num2}`, answer };
};

const Investors = () => {
  const { t } = useLanguage();

  // Investor Contact Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    investmentRange: '',
    message: '',
    gdprConsent: false
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captcha, setCaptcha] = useState({ question: '', answer: 0 });
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaError, setCaptchaError] = useState(false);

  const refreshCaptcha = useCallback(() => {
    setCaptcha(generateCaptcha());
    setCaptchaInput('');
    setCaptchaError(false);
  }, []);

  useEffect(() => {
    refreshCaptcha();
  }, [refreshCaptcha]);

  // Handle hash scroll for #leadership anchor
  useEffect(() => {
    if (window.location.hash === '#leadership') {
      setTimeout(() => {
        const element = document.getElementById('leadership');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });
    setCaptchaError(false);

    // Verify captcha
    if (parseInt(captchaInput) !== captcha.answer) {
      setCaptchaError(true);
      setIsSubmitting(false);
      setStatus({
        type: 'error',
        message: t(
          'Incorrect answer. Please try again.',
          'Respuesta incorrecta. Por favor, inténtalo de nuevo.'
        )
      });
      refreshCaptcha();
      return;
    }

    try {
      const response = await fetch('/contacto.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, form: 'investors', website: '' })
      });
      const result = await response.json().catch(() => ({ ok: false }));
      if (!response.ok || !result.ok) {
        throw new Error((result && result.error) || 'Server error');
      }
      
      setStatus({
        type: 'success',
        message: t(
          'Thank you for your interest. Our investor relations team will contact you within 48 hours.',
          'Gracias por su interés. Nuestro equipo de relaciones con inversores le contactará en 48 horas.'
        )
      });
      setFormData({ name: '', email: '', company: '', investmentRange: '', message: '', gdprConsent: false });
      refreshCaptcha();
    } catch (error) {
      setStatus({
        type: 'error',
        message: t(
          'There was an error sending your message. Please try again.',
          'Hubo un error al enviar tu mensaje. Por favor, inténtalo de nuevo.'
        )
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const investmentRanges = [
    { value: '', label: t('Select investment range', 'Seleccione rango de inversión') },
    { value: '10k-50k', label: '€10K - €50K' },
    { value: '50k-100k', label: '€50K - €100K' },
    { value: '100k-250k', label: '€100K - €250K' },
    { value: '250k+', label: '€250K+' },
    { value: 'undisclosed', label: t('Prefer not to disclose', 'Prefiero no revelar') }
  ];

  const candidateClasses = [
    {
      icon: Shield,
      title: t('Checkpoint Inhibitor Conjugates', 'Conjugados de Inhibidores Checkpoint'),
      subtitle: t('Designed to Convert "Cold" Tumors to Immunotherapy-Responsive (Investigational)', 'Diseñados para Convertir Tumores "Fríos" en Respondedores a Inmunoterapia (Investigacional)'),
      market: '€14.4B/year',
      description: t(
        'Addresses 50-70% of solid tumor patients who don\'t respond to current checkpoint inhibitors. The investigational dual immuno-metabolic approach is designed to reduce tumor lactate production, potentially reversing T-cell exhaustion and enabling response to PD-1/PD-L1 blockade. Applicable to multiple solid tumor types.',
        'Aborda el 50-70% de pacientes con tumores sólidos que no responden a inhibidores de puntos de control actuales. El enfoque investigacional dual inmuno-metabólico está diseñado para reducir la producción de lactato tumoral, potencialmente revirtiendo el agotamiento de células T y permitiendo respuesta al bloqueo PD-1/PD-L1. Aplicable a múltiples tipos de tumores sólidos.'
      ),
      status: t('Design complete, synthesis Q2 2025', 'Diseño completo, síntesis Q2 2025'),
      color: 'blue'
    },
    {
      icon: Activity,
      title: t('Enhanced Chemotherapeutic Conjugates', 'Conjugados Quimioterapéuticos Mejorados'),
      subtitle: t('Designed to Reduce Systemic Toxicity While Maintaining Efficacy (Investigational)', 'Diseñados para Reducir Toxicidad Sistémica Manteniendo Eficacia (Investigacional)'),
      market: '€2.7B/year',
      description: t(
        'The investigational platform aims to concentrate chemotherapy in tumor cells through metabolic targeting, potentially reducing systemic exposure. Design goal: reduce cardiotoxicity, nephrotoxicity, and neuropathy while maintaining therapeutic outcomes. Quantitative toxicity reduction to be determined in preclinical and clinical studies.',
        'La plataforma investigacional busca concentrar la quimioterapia en células tumorales mediante direccionamiento metabólico, potencialmente reduciendo la exposición sistémica. Objetivo de diseño: reducir cardiotoxicidad, nefrotoxicidad y neuropatía manteniendo resultados terapéuticos. Reducción cuantitativa de toxicidad a determinar en estudios preclínicos y clínicos.'
      ),
      status: t('POC fluorophore validation ongoing (€15K)', 'Validación POC fluoróforo en curso (€15K)'),
      color: 'purple'
    },
    {
      icon: Zap,
      title: t('Theranostic Conjugates', 'Conjugados Teragnósticos'),
      subtitle: t('Diagnostic + Therapeutic in Single Molecule', 'Diagnóstico + Terapéutico en Molécula Única'),
      market: '€600M/year',
      description: t(
        'Dual-function integration enables real-time visualization of drug distribution and personalized dosing based on actual tumor uptake. Same molecule provides PET/SPECT imaging and therapeutic delivery, confirming targeting before toxicity.',
        'La integración de función dual permite visualización en tiempo real de la distribución del fármaco y dosificación personalizada basada en captación tumoral real. La misma molécula proporciona imagen PET/SPECT y entrega terapéutica, confirmando direccionamiento antes de toxicidad.'
      ),
      status: t('Conceptual design, synthesis planned 2026', 'Diseño conceptual, síntesis planificada 2026'),
      color: 'green'
    },
    {
      icon: Heart,
      title: t('Repositioned Small Molecules', 'Moléculas Pequeñas Reposicionadas'),
      subtitle: t('Accelerated Development with Known Safety Profiles', 'Desarrollo Acelerado con Perfiles de Seguridad Conocidos'),
      market: t('Multiple indications', 'Múltiples indicaciones'),
      description: t(
        'Modification of FDA/EMA-approved drugs for oncology via metabolic conjugation. Safety profiles established, enabling expedited regulatory pathways (505(b)(2)/hybrid). Development costs 50-70% lower than de novo with extended patent life. Accelerated path for treating solid tumors, leukemias, and rare cancers including orphan designation opportunities.',
        'Modificación de fármacos aprobados por FDA/EMA para oncología vía conjugación metabólica. Perfiles de seguridad establecidos, permitiendo vías regulatorias expeditas (505(b)(2)/híbrido). Costos de desarrollo 50-70% menores que de novo con vida de patente extendida. Vía acelerada para tratar tumores sólidos, leucemias y tumores raros incluyendo oportunidades de designación huérfana.'
      ),
      status: t('Multiple candidates in patent application', 'Múltiples candidatos en solicitud de patente'),
      color: 'yellow'
    }
  ];

  return (
    <div className="bg-white">
      {/* Seed Round Announcement - Highlighted */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#FFD700] to-amber-400">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h2 className="text-2xl md:text-3xl font-bold text-[#1F4E78] mb-2">
                {t('🚀 Strategic Seed Round Open | Potential Return 10-50×', '🚀 Ronda Seed Estratégica Abierta | Retorno Potencial 10-50×')}
              </h2>
              <p className="text-lg text-[#1F4E78]/80">
                {t(
                  'Pre-POC preparation round: €330K to crystallize valuation ahead of $15-20M POC financing.',
                  'Ronda de preparación pre-POC: €330K para cristalizar valoración antes de financiación POC de $15-20M.'
                )}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="bg-white/90 backdrop-blur-sm rounded-lg px-6 py-3 text-center">
                <div className="text-2xl font-bold text-[#1F4E78]">€330K</div>
                <div className="text-sm text-gray-600">{t('Seed Round', 'Ronda Seed')}</div>
              </div>
              <div className="bg-white/90 backdrop-blur-sm rounded-lg px-6 py-3 text-center">
                <div className="text-2xl font-bold text-[#1F4E78]">€40-50M</div>
                <div className="text-sm text-gray-600">{t('Pre-Money', 'Pre-Money')}</div>
              </div>
              <div className="bg-white/90 backdrop-blur-sm rounded-lg px-6 py-3 text-center">
                <div className="text-2xl font-bold text-[#1F4E78]">6</div>
                <div className="text-sm text-gray-600">{t('Months to POC', 'Meses a POC')}</div>
              </div>
              <div className="bg-white/90 backdrop-blur-sm rounded-lg px-6 py-3 text-center">
                <div className="text-2xl font-bold text-green-600">10-50×</div>
                <div className="text-sm text-gray-600">{t('Potential Return', 'Retorno Potencial')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Section - Keep existing code */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1F4E78] via-[#2E5090] to-[#1F4E78] text-white overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        {/* Background Image - Financial Growth */}
        <div className="absolute inset-0 opacity-15">
          <img 
            src="https://customer-assets.emergentagent.com/job_metasigna-biotech/artifacts/ovhonzee_o3qE0tLUktyQBDZkIMcWl.png"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <p className="text-[#FFD700] font-semibold mb-2 tracking-wide">METASIGNA THERAPEUTICS</p>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {t(
              'Portfolio Platinum – Strategic Seed Opportunity',
              'Portfolio Platinum – Oportunidad Seed Estratégica'
            )}
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-4xl">
            {t(
              'A first-in-class GLUT1-targeted metabolic conjugate platform in oncology',
              'Plataforma de conjugados metabólicos dirigidos a GLUT1 de primera clase en oncología'
            )}
          </p>

          {/* Key Stats for Seed Round */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="text-2xl font-bold text-[#FFD700]">€330K</div>
              <div className="text-sm text-gray-200">{t('Seed Round', 'Ronda Seed')}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="text-2xl font-bold text-[#FFD700]">€40-50M</div>
              <div className="text-sm text-gray-200">{t('Pre-Money', 'Pre-Money')}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="text-2xl font-bold text-[#FFD700]">6</div>
              <div className="text-sm text-gray-200">{t('Months to POC Round', 'Meses a Ronda POC')}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="text-2xl font-bold text-[#FFD700]">$15-20M</div>
              <div className="text-sm text-gray-200">{t('Next Round (POC)', 'Próxima Ronda (POC)')}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="text-2xl font-bold text-green-400">€50K+</div>
              <div className="text-sm text-gray-200">{t('Min. Ticket', 'Ticket Mín.')}</div>
            </div>
          </div>

          {/* CTAs - Simplified */}
          <div className="flex flex-col sm:flex-row gap-4">
            <a 
              href="#investor-form"
              className="flex items-center justify-center space-x-2 px-8 py-4 bg-[#FFD700] text-[#1F4E78] rounded-lg font-semibold hover:bg-yellow-400 transition-colors text-lg"
            >
              <Mail size={22} />
              <span>{t('Request Investor Materials', 'Solicitar Materiales de Inversión')}</span>
            </a>
            <a href="mailto:investors@metasigna.company" className="flex items-center justify-center space-x-2 px-8 py-4 bg-white/10 backdrop-blur-sm border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-[#1F4E78] transition-colors text-lg">
              <Calendar size={22} />
              <span>{t('Schedule Intro Call', 'Agendar Llamada Introductoria')}</span>
            </a>
          </div>
        </div>
      </section>

      {/* THE OPPORTUNITY Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-[#1F4E78]">
            {t('The Opportunity', 'La Oportunidad')}
          </h2>
          <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              {t(
                'Metasigna Therapeutics is developing Portfolio Platinum, a proprietary platform of GLUT1-targeted metabolic conjugates designed to overcome one of oncology\'s core limitations: selective delivery into metabolically reprogrammed tumor cells.',
                'Metasigna Therapeutics está desarrollando Portfolio Platinum, una plataforma propietaria de conjugados metabólicos dirigidos a GLUT1 diseñada para superar una de las limitaciones principales de la oncología: la entrega selectiva en células tumorales metabólicamente reprogramadas.'
              )}
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              {t(
                'Unlike single-asset biotechs, Portfolio Platinum is conceived as a platform strategy, comprising multiple conjugates and rational combinations built on the same biological transport mechanism.',
                'A diferencia de las biotechs de activo único, Portfolio Platinum está concebido como una estrategia de plataforma, que comprende múltiples conjugados y combinaciones racionales construidas sobre el mismo mecanismo de transporte biológico.'
              )}
            </p>

            {/* Orphan Indications Highlight */}
            <div className="bg-gradient-to-r from-rose-50 to-amber-50 rounded-xl p-6 mb-8 border border-rose-100">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-rose-500 to-amber-500 rounded-lg flex items-center justify-center">
                  <Heart className="text-white" size={24} />
                </div>
                <div>
                  <p className="text-gray-700 leading-relaxed">
                    {t(
                      'The Metasigna platform enables development of metabolically-targeted therapies across a broad spectrum of solid tumors and hematological malignancies. Within this scope, we strategically prioritize orphan indications and oncology subpopulations with critical unmet medical need —such as glioblastoma, certain aggressive leukemias, or metastatic triple-negative breast cancer subgroups— where accelerated regulatory pathways offer a clear competitive advantage and potentially faster access to patients.',
                      'La plataforma Metasigna permite el desarrollo de terapias metabólicamente dirigidas a un amplio espectro de tumores sólidos y neoplasias hematológicas. En este contexto, priorizamos estratégicamente indicaciones huérfanas y subpoblaciones oncológicas con necesidad médica crítica no cubierta —como el glioblastoma, determinadas leucemias agresivas o subgrupos de cáncer de mama triple negativo metastásico— donde las vías regulatorias aceleradas ofrecen una ventaja competitiva clara y un acceso potencialmente más rápido a los pacientes.'
                    )}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <span className="px-3 py-1 bg-rose-100 text-rose-700 rounded-full text-xs font-medium">
                      {t('Glioblastoma', 'Glioblastoma')}
                    </span>
                    <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                      {t('Aggressive Leukemias', 'Leucemias Agresivas')}
                    </span>
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                      {t('Triple-Negative Breast Cancer', 'Cáncer de Mama Triple Negativo')}
                    </span>
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                      {t('Orphan Designations', 'Designaciones Huérfanas')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-blue-50 p-6 rounded-lg">
                <Target className="text-[#1F4E78] mb-3" size={28} />
                <h4 className="font-semibold text-gray-800 mb-2">{t('Indications', 'Indicaciones')}</h4>
                <p className="text-gray-600">{t('Solid tumors with high GLUT1 overexpression', 'Tumores sólidos con alta sobreexpresión de GLUT1')}</p>
              </div>
              <div className="bg-blue-50 p-6 rounded-lg">
                <Activity className="text-[#1F4E78] mb-3" size={28} />
                <h4 className="font-semibold text-gray-800 mb-2">{t('Patients', 'Pacientes')}</h4>
                <p className="text-gray-600">{t('~1 million new cases/year globally', '~1 millón de casos nuevos/año globalmente')}</p>
              </div>
              <div className="bg-blue-50 p-6 rounded-lg">
                <TrendingUp className="text-[#1F4E78] mb-3" size={28} />
                <h4 className="font-semibold text-gray-800 mb-2">{t('Addressable Market', 'Mercado Direccionable')}</h4>
                <p className="text-gray-600 text-xl font-bold">$146–217B</p>
              </div>
              <div className="bg-blue-50 p-6 rounded-lg">
                <Zap className="text-[#1F4E78] mb-3" size={28} />
                <h4 className="font-semibold text-gray-800 mb-2">{t('Platform Scope', 'Alcance de Plataforma')}</h4>
                <p className="text-gray-600">{t('4 lead conjugates + 3 combinations', '4 conjugados principales + 3 combinaciones')}</p>
              </div>
              <div className="bg-blue-50 p-6 rounded-lg">
                <Shield className="text-[#1F4E78] mb-3" size={28} />
                <h4 className="font-semibold text-gray-800 mb-2">{t('IP Status', 'Estado de PI')}</h4>
                <p className="text-gray-600">{t('Priority patent filed (OEPM, Sept 2025); PCT scheduled Sept 2026', 'Patente prioritaria presentada (OEPM, Sept 2025); PCT programada Sept 2026')}</p>
              </div>
              <div className="bg-green-50 p-6 rounded-lg border-2 border-green-200">
                <Heart className="text-green-600 mb-3" size={28} />
                <h4 className="font-semibold text-gray-800 mb-2">{t('Life-Years Impact', 'Impacto en Años de Vida')}</h4>
                <p className="text-green-700 text-xl font-bold">1.2-4.2M {t('/year', '/año')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY GLUT1? Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-[#1F4E78]">
            {t('Why GLUT1?', '¿Por qué GLUT1?')}
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                {t(
                  'GLUT1 is a glucose transporter consistently overexpressed in aggressive and treatment-resistant tumors. It represents a convergent vulnerability across oncology indications.',
                  'GLUT1 es un transportador de glucosa consistentemente sobreexpresado en tumores agresivos y resistentes al tratamiento. Representa una vulnerabilidad convergente a través de indicaciones oncológicas.'
                )}
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                {t(
                  'Portfolio Platinum exploits this vulnerability by chemically conjugating antitumoral agents to GLUT1-captured metabolic ligands, enabling:',
                  'Portfolio Platinum explota esta vulnerabilidad conjugando químicamente agentes antitumorales a ligandos metabólicos capturados por GLUT1, permitiendo:'
                )}
              </p>
              <ul className="space-y-4">
                {[
                  t('Preferential tumor uptake', 'Captación tumoral preferencial'),
                  t('Reduced systemic exposure', 'Exposición sistémica reducida'),
                  t('Platform-level scalability across multiple drugs', 'Escalabilidad a nivel de plataforma a través de múltiples fármacos')
                ].map((item, i) => (
                  <li key={i} className="flex items-center space-x-3">
                    <CheckCircle className="text-green-500 flex-shrink-0" size={24} />
                    <span className="text-gray-700 text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PLATFORM ADVANTAGE Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-[#1F4E78]">
            {t('Platform Advantage', 'Ventaja de Plataforma')}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[
              {
                icon: Target,
                title: t('First-in-Class', 'Primero en su Clase'),
                desc: t('Metabolic transporter targeting (not receptor)', 'Direccionamiento de transportador metabólico (no receptor)')
              },
              {
                icon: Shield,
                title: t('Multi-Asset IP', 'PI Multi-Activo'),
                desc: t('Multiple assets within single IP family', 'Múltiples activos dentro de una familia de PI única')
              },
              {
                icon: Zap,
                title: t('Drug Compatibility', 'Compatibilidad de Fármacos'),
                desc: t('Works with existing and repurposed drugs', 'Funciona con fármacos existentes y reposicionados')
              },
              {
                icon: TrendingUp,
                title: t('Clear Exit Path', 'Camino de Salida Claro'),
                desc: t('Oncology pharma, ADC/delivery players', 'Farma oncológica, jugadores de ADC/entrega')
              }
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
                <item.icon className="text-[#1F4E78] mb-4" size={32} />
                <h4 className="font-bold text-gray-800 mb-2">{item.title}</h4>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="bg-[#FFD700]/20 border border-[#FFD700] rounded-xl p-6">
            <p className="text-lg text-[#1F4E78] font-semibold text-center">
              {t(
                'Comparable delivery platforms have historically achieved post-POC valuations in the $500M–$2B range, with blockbuster upside for lead assets.',
                'Plataformas de entrega comparables han logrado históricamente valoraciones post-POC en el rango de $500M–$2B, con potencial blockbuster para activos principales.'
              )}
            </p>
          </div>
        </div>
      </section>

      {/* CURRENT ROUND Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1F4E78] via-[#2E5090] to-[#1F4E78] text-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {t('Current Round: Strategic Seed', 'Ronda Actual: Seed Estratégica')}
          </h2>
          <p className="text-xl text-gray-200 mb-8 max-w-3xl">
            {t(
              'Metasigna is opening a Strategic Seed round to complete pre-POC preparation and position the company for an institutional POC financing.',
              'Metasigna está abriendo una ronda Seed Estratégica para completar la preparación pre-POC y posicionar la empresa para una financiación POC institucional.'
            )}
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-[#FFD700] mb-2">€330,000</div>
              <div className="text-gray-200">{t('Round Size', 'Tamaño de Ronda')}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-[#FFD700] mb-2">€40-50M</div>
              <div className="text-gray-200">{t('Pre-Money Valuation', 'Valoración Pre-Money')}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-[#FFD700] mb-2">6</div>
              <div className="text-gray-200">{t('Months to POC-Round Readiness', 'Meses hasta Preparación POC')}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-[#FFD700] mb-2">€50K+</div>
              <div className="text-gray-200">{t('Minimum Ticket', 'Ticket Mínimo')}</div>
            </div>
          </div>
          <div className="bg-white/5 rounded-xl p-6 border border-white/10 mb-8">
            <h4 className="text-lg font-semibold text-[#FFD700] mb-4">{t('Use of Funds', 'Uso de Fondos')}</h4>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                t('PCT filing', 'Presentación PCT'),
                t('CRO selection', 'Selección de CRO'),
                t('POC study design', 'Diseño de estudio POC'),
                t('Pharma & investor outreach', 'Acercamiento a farma e inversores')
              ].map((item, i) => (
                <div key={i} className="flex items-center space-x-2">
                  <CheckCircle className="text-[#FFD700] flex-shrink-0" size={18} />
                  <span className="text-gray-200">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-[#FFD700] text-[#1F4E78] rounded-xl p-6">
            <p className="text-lg font-semibold text-center">
              {t(
                'This round is not survival capital. It is designed to crystallize valuation ahead of a $15–20M POC round.',
                'Esta ronda no es capital de supervivencia. Está diseñada para cristalizar la valoración antes de una ronda POC de $15–20M.'
              )}
            </p>
          </div>
        </div>
      </section>

      {/* UPCOMING VALUE INFLECTION Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-[#1F4E78]">
            {t('Upcoming Value Inflection', 'Próxima Inflexión de Valor')}
          </h2>
          <p className="text-lg text-gray-600 mb-8">{t('Within 6–9 months:', 'En 6–9 meses:')}</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {[
              { icon: CheckCircle, text: t('Institutional-grade POC protocol finalized', 'Protocolo POC de grado institucional finalizado') },
              { icon: CheckCircle, text: t('CRO contracted (EU-based, oncology-experienced)', 'CRO contratado (con sede en UE, experiencia en oncología)') },
              { icon: CheckCircle, text: t('Full valuation model and comparables completed', 'Modelo de valoración completo y comparables finalizados') },
              { icon: CheckCircle, text: t('Pharma BD discussions initiated', 'Discusiones BD con farma iniciadas') },
              { icon: CheckCircle, text: t('POC round ($15–20M) launched', 'Ronda POC ($15–20M) lanzada') }
            ].map((item, i) => (
              <div key={i} className="flex items-start space-x-3 bg-gray-50 p-4 rounded-lg">
                <item.icon className="text-green-500 flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700">{item.text}</span>
              </div>
            ))}
          </div>
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl p-6 text-center">
            <p className="text-xl font-bold">
              {t('Targeted next valuation step: €80–120M pre-POC execution', 'Próximo paso de valoración objetivo: €80–120M pre-ejecución POC')}
            </p>
          </div>
        </div>
      </section>

      {/* INVESTOR PROFILE Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-[#1F4E78]">
            {t('Target Investors', 'Inversores Objetivo')}
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            {t(
              'We are currently targeting strategic investors with experience in biotechnology and oncology, including biotech angels, specialized family offices, and early-stage funds with interest in therapeutic platforms and accelerated regulatory pathways.',
              'Actualmente nos dirigimos a inversores estratégicos con experiencia en biotecnología y oncología, incluyendo biotech angels, family offices especializados y fondos early-stage, con interés en plataformas terapéuticas y vías regulatorias aceleradas.'
            )}
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[
              { title: t('Biotech Angels', 'Biotech Angels'), desc: t('With sector expertise', 'Con experiencia en el sector') },
              { title: t('Specialized', 'Especializados'), desc: t('Family Offices', 'Family Offices') },
              { title: t('Early-Stage', 'Early-Stage'), desc: t('Funds', 'Fondos') },
              { title: t('Oncology', 'Oncología'), desc: t('Focused Investors', 'Inversores Enfocados') }
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-md border border-gray-100 text-center">
                <h4 className="font-bold text-[#1F4E78] text-lg">{item.title}</h4>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="bg-[#1F4E78] text-white rounded-xl p-6 flex flex-col md:flex-row items-center justify-between">
            <div>
              <p className="text-xl font-bold mb-2">{t('Minimum Investment €50K', 'Inversión Mínima €50K')}</p>
              <p className="text-gray-300">{t('Pro-rata rights into the POC round available.', 'Derechos pro-rata para la ronda POC disponibles.')}</p>
            </div>
            <a href="#investor-form" className="mt-4 md:mt-0 px-6 py-3 bg-[#FFD700] text-[#1F4E78] rounded-lg font-semibold hover:bg-yellow-400 transition-colors">
              {t('Express Interest', 'Expresar Interés')}
            </a>
          </div>
        </div>
      </section>

      {/* Problem Section - Keep existing */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-[#1F4E78]">
            {t('The Immuno-Oncology Challenge', 'El Desafío de la Inmuno-Oncología')}
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                {t(
                  'Despite revolutionary advances in cancer immunotherapy, 50-70% of solid tumors remain unresponsive to checkpoint inhibitors. The culprit? An immunosuppressive tumor microenvironment driven by lactate accumulation.',
                  'A pesar de los avances revolucionarios en inmunoterapia del cáncer, el 50-70% de tumores sólidos siguen sin responder a inhibidores de puntos de control. ¿El culpable? Un microambiente tumoral inmunosupresor impulsado por acumulación de lactato.'
                )}
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                {t(
                  'Current solutions face significant challenges: ADCs carry cardiotoxicity concerns, small molecules lack tumor selectivity, and combination therapies amplify side effects.',
                  'Las soluciones actuales enfrentan desafíos significativos: los ADCs conllevan riesgos de cardiotoxicidad, las moléculas pequeñas carecen de selectividad tumoral, y las terapias combinadas amplifican los efectos secundarios.'
                )}
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Market Opportunity */}
              <div className="bg-blue-50 p-8 rounded-lg border border-blue-100">
                <h3 className="text-xl font-semibold mb-4 text-[#1F4E78]">
                  {t('Market Opportunity', 'Oportunidad de Mercado')}
                </h3>
                <div className="space-y-4">
                  <div>
                    <div className="text-3xl font-bold text-[#2E5090] mb-1">€20B+</div>
                    <p className="text-sm text-gray-600">
                      {t('Immuno-metabolic therapeutics market', 'Mercado de terapéuticas inmuno-metabólicas')}
                    </p>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-[#2E5090] mb-1">50-70%</div>
                    <p className="text-sm text-gray-600">
                      {t('Non-responder population to checkpoint inhibitors', 'Población no respondedora a inhibidores checkpoint')}
                    </p>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-[#2E5090] mb-1">2028+</div>
                    <p className="text-sm text-gray-600">
                      {t('Repatenting proven drugs as upgraded versions: more effective, less toxic', 'Repatentando fármacos probados como versiones mejoradas: más efectivos, menos tóxicos')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Therapeutic Impact - Portfolio Platinum */}
              <div className="bg-amber-50 p-8 rounded-lg border border-amber-200">
                <h3 className="text-xl font-semibold mb-4 text-amber-800">
                  {t('Therapeutic Impact', 'Impacto Terapéutico')}
                </h3>
                <div className="space-y-4">
                  <div>
                    <div className="text-3xl font-bold text-amber-700 mb-1">1.1M - 4.2M</div>
                    <p className="text-sm text-gray-700">
                      {t('Life-years gained annually (from conservative to high-impact scenario)', 'Años de vida ganados anualmente (de escenario conservador a alto impacto)')}
                    </p>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-amber-700 mb-1">Portfolio Platinum</div>
                    <p className="text-sm text-gray-700">
                      {t('Coherent therapeutic platform — not just drug repositioning. Each conjugate is stronger in combination.', 'Plataforma terapéutica coherente — no solo reposicionamiento de fármacos. Cada conjugado es más fuerte en combinación.')}
                    </p>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-amber-700 mb-1">{t('Dual Mechanism', 'Mecanismo Dual')}</div>
                    <p className="text-sm text-gray-700">
                      {t('The vector provides metabolic toxicity to tumor cells + targeted payload delivery — the enabling technological asset.', 'El vector aporta toxicidad metabólica a células tumorales + entrega dirigida de carga útil — el activo tecnológico habilitador.')}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-amber-600 mt-4 italic">
                  {t('* Impact study available upon request', '* Estudio de impacto disponible bajo solicitud')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#1F4E78]">
            {t('Dual-Function Metabolic Targeting: Vectorial + Therapeutic', 'Direccionamiento Metabólico de Doble Función: Vectorial + Terapéutica')}
          </h2>
          <p className="text-xl font-semibold text-gray-700 mb-8">
            {t('Metabolic Ligands That Simultaneously Target AND Attack Tumors', 'Ligandos Metabólicos que Simultáneamente Dirigen Y Atacan Tumores')}
          </p>
          <p className="text-xl text-gray-700 mb-12 max-w-4xl">
            {t(
              'The investigational Clivamed® platform represents a novel approach to oncology: therapeutic metabolic ligands designed with dual functionality - vectorial (tumor targeting) and therapeutic (metabolic disruption). Unlike conventional approaches using inert "carrier" molecules, our ligands are designed to be metabolic disruptors that also serve as tumor-selective delivery vectors.',
              'La plataforma investigacional Clivamed® representa un enfoque novedoso en oncología: ligandos metabólicos terapéuticos diseñados con doble funcionalidad - vectorial (direccionamiento tumoral) y terapéutica (disrupción metabólica). A diferencia de enfoques convencionales que usan moléculas "portadoras" inertes, nuestros ligandos están diseñados para ser disruptores metabólicos que también sirven como vectores de entrega selectiva tumoral.'
            )}
          </p>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <Target size={32} className="text-[#1F4E78]" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-[#1F4E78]">
                {t('1. Tumor-Preferential Accumulation', '1. Acumulación Preferencial Tumoral')}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {t(
                  'Cancer cells overexpress nutrient transporters to fuel rapid growth. Our investigational ligands are designed to exploit this dependency, similar to how FDG-PET imaging works (validated by 40M+ annual procedures). Quantitative selectivity being determined in POC studies.',
                  'Las células cancerosas sobreexpresan transportadores de nutrientes para impulsar el crecimiento rápido. Nuestros ligandos investigacionales están diseñados para explotar esta dependencia, similar a cómo funciona la imagen FDG-PET (validada por más de 40M de procedimientos anuales). Selectividad cuantitativa siendo determinada en estudios POC.'
                )}
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <Shield size={32} className="text-[#1F4E78]" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-[#1F4E78]">
                {t('2. Metabolic Disruption (Design Intent)', '2. Disrupción Metabólica (Objetivo de Diseño)')}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {t(
                  'Upon cellular entry, our investigational ligands are designed to disrupt tumor metabolism through multiple mechanisms: ATP depletion, glycolysis inhibition, lactate secretion blockade, ROS generation, and glutathione depletion. Mechanism confirmation ongoing in POC studies.',
                  'Al entrar a la célula, nuestros ligandos investigacionales están diseñados para interrumpir el metabolismo tumoral mediante múltiples mecanismos: depleción de ATP, inhibición de glucólisis, bloqueo de secreción de lactato, generación de ROS y depleción de glutatión. Confirmación de mecanismo en curso en estudios POC.'
                )}
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <TrendingUp size={32} className="text-[#1F4E78]" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-[#1F4E78]">
                {t('3. Intratumoral Drug Release', '3. Liberación Intratumoral de Fármaco')}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {t(
                  'Tumor microenvironment conditions (acidic pH 6.8-6.9, elevated glutathione, tumor-specific proteases) are designed to cleave the conjugate linker, releasing the therapeutic payload where needed. Stable in circulation → Activated in tumor.',
                  'Las condiciones del microambiente tumoral (pH ácido 6.8-6.9, glutatión elevado, proteasas específicas tumorales) están diseñadas para escindir el enlazador del conjugado, liberando la carga terapéutica donde se necesita. Estable en circulación → Activado en tumor.'
                )}
              </p>
            </div>
          </div>

          {/* Why This Matters */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 mb-8">
            <h3 className="text-2xl font-bold mb-4 text-[#1F4E78]">
              {t('Multi-Mechanism Design to Reduce Resistance Risk', 'Diseño Multi-Mecanismo para Reducir Riesgo de Resistencia')}
            </h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <p className="font-semibold text-gray-800 mb-2">
                  {t('Conventional approaches: Single mechanism', 'Enfoques convencionales: Mecanismo único')}
                </p>
                <p className="text-gray-600 text-sm mb-4">
                  {t('Either metabolic OR drug → Tumor may adapt, develop resistance', 'O metabólico O fármaco → El tumor puede adaptarse, desarrollar resistencia')}
                </p>
              </div>
              <div>
                <p className="font-semibold text-gray-800 mb-2">
                  {t('Our investigational approach: Triple mechanism design', 'Nuestro enfoque investigacional: Diseño triple mecanismo')}
                </p>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• {t('Metabolic disruption (ligand effect)', 'Disrupción metabólica (efecto del ligando)')}</li>
                  <li>• {t('Primary therapeutic (payload effect)', 'Terapéutico primario (efecto de carga útil)')}</li>
                  <li>• {t('Potential immune activation (microenvironment modulation)', 'Potencial activación inmune (modulación microambiente)')}</li>
                </ul>
              </div>
            </div>
            <p className="text-gray-700 mt-4 font-semibold">
              {t(
                'Design Rationale: While resistance can develop to any cancer therapy, the multi-mechanism approach is designed to raise the barrier to resistance compared to single-target therapies. Clinical validation required.',
                'Fundamento de diseño: Aunque la resistencia puede desarrollarse a cualquier terapia de cáncer, el enfoque multi-mecanismo está diseñado para elevar la barrera a la resistencia comparado con terapias de objetivo único. Validación clínica requerida.'
              )}
            </p>
          </div>

          {/* Platform Architecture */}
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <h3 className="text-2xl font-bold mb-6 text-[#1F4E78]">
              {t('Platform Architecture - Modular Design', 'Arquitectura de Plataforma - Diseño Modular')}
            </h3>
            <div className="text-center mb-6">
              <div className="inline-flex items-center space-x-4 text-lg font-mono bg-gray-50 p-4 rounded-lg">
                <div className="text-center">
                  <div className="font-bold text-[#2E5090]">[{t('Therapeutic Ligand', 'Ligando Terapéutico')}]</div>
                  <div className="text-sm text-gray-600 mt-2">{t('Dual-function:', 'Doble función:')}</div>
                  <div className="text-sm text-gray-600">{t('Targeting + Attack', 'Direccionamiento + Ataque')}</div>
                  <div className="text-xl font-bold text-[#2E5090] mt-2">5 {t('OPTIONS', 'OPCIONES')}</div>
                </div>
                <div className="text-2xl text-gray-400">←→</div>
                <div className="text-center">
                  <div className="font-bold text-[#2E5090]">[{t('Cleavable Linker', 'Enlazador Escindible')}]</div>
                  <div className="text-sm text-gray-600 mt-2">{t('Tumor-activated:', 'Activado en tumor:')}</div>
                  <div className="text-sm text-gray-600">pH, GSH, Protease</div>
                  <div className="text-xl font-bold text-[#2E5090] mt-2">3 {t('OPTIONS', 'OPCIONES')}</div>
                </div>
                <div className="text-2xl text-gray-400">←→</div>
                <div className="text-center">
                  <div className="font-bold text-[#2E5090]">[{t('Drug Payload', 'Carga Útil')}]</div>
                  <div className="text-sm text-gray-600 mt-2">{t('Modular:', 'Modular:')}</div>
                  <div className="text-sm text-gray-600">{t('Swappable', 'Intercambiable')}</div>
                  <div className="text-xl font-bold text-[#2E5090] mt-2">10+ {t('OPTIONS', 'OPCIONES')}</div>
                </div>
              </div>
            </div>
            <p className="text-center text-lg font-semibold text-gray-700 mb-4">
              {t(
                '5 therapeutic ligands × 3 linker types × 10 drug payloads = 150 potential conjugates',
                '5 ligandos terapéuticos × 3 tipos de enlazadores × 10 cargas de fármacos = 150 conjugados potenciales'
              )}
            </p>
            <p className="text-center text-gray-600">
              {t(
                'Current patent application covers 47 priority conjugates. Specific ligand chemistry and drug combinations disclosed under NDA to qualified investors.',
                'La solicitud de patente actual cubre 47 conjugados prioritarios. Química específica de ligandos y combinaciones de fármacos reveladas bajo NDA a inversores calificados.'
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Clinical Validation Section - NEW */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-[#1F4E78]">
            {t('Clinical Validation - 50+ Years Precedent', 'Validación Clínica - Precedente de 50+ Años')}
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-8">
              <h3 className="text-xl font-semibold mb-4 text-[#1F4E78]">
                {t('Metabolic Targeting: Clinically Proven Approach', 'Direccionamiento Metabólico: Enfoque Clínicamente Probado')}
              </h3>
              <p className="text-gray-700 mb-4">
                {t(
                  'The principle of exploiting tumor metabolic vulnerabilities for selective accumulation has been extensively validated in clinical oncology for over five decades.',
                  'El principio de explotar las vulnerabilidades metabólicas tumorales para la acumulación selectiva ha sido ampliamente validado en oncología clínica durante más de cinco décadas.'
                )}
              </p>
              <div className="bg-white rounded-lg p-4 mb-4">
                <p className="font-semibold text-gray-800 mb-2">FDG-PET Imaging (1970s-Present):</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• 40+ million procedures performed annually worldwide</li>
                  <li>• Gold standard for cancer detection, staging, and monitoring</li>
                  <li>• Demonstrates 5-10x tumor vs. normal tissue selectivity</li>
                  <li>• Proves translatability: metabolic targeting works in humans</li>
                </ul>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <h3 className="text-xl font-semibold mb-4 text-[#1F4E78]">
                {t('Our Innovation: From Diagnostic to Therapeutic', 'Nuestra Innovación: De Diagnóstico a Terapéutico')}
              </h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-[#2E5090] rounded-full"></div>
                  <p className="text-gray-700">
                    <span className="font-semibold">FDG-PET:</span> {t('Metabolic targeting for imaging only (inert tracer)', 'Direccionamiento metabólico solo para imagen (trazador inerte)')}
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-[#FFD700] rounded-full"></div>
                  <p className="text-gray-700">
                    <span className="font-semibold">Clivamed®:</span> {t('Metabolic targeting for dual therapy (active ligands)', 'Direccionamiento metabólico para terapia dual (ligandos activos)')}
                  </p>
                </div>
              </div>
              <div className="mt-6 bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  {t(
                    'The translatability risk is dramatically de-risked by 50+ years of clinical experience demonstrating that metabolic transporter targeting is safe, reproducible, and selective across cancer types.',
                    'El riesgo de trasladabilidad se reduce drásticamente con más de 50 años de experiencia clínica que demuestra que el direccionamiento de transportadores metabólicos es seguro, reproducible y selectivo en tipos de cáncer.'
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pipeline Section - NEW COMPLETE */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center text-[#1F4E78]">
            {t('Four Therapeutic Classes - Platform-in-a-Pipeline', 'Cuatro Clases Terapéuticas - Plataforma-en-Pipeline')}
          </h2>
          <p className="text-xl text-gray-600 mb-8 text-center max-w-4xl mx-auto">
            {t(
              'Modular architecture enables rapid generation of multiple candidates by swapping payload components while maintaining core dual-function metabolic targeting.',
              'La arquitectura modular permite la generación rápida de múltiples candidatos intercambiando componentes de carga útil mientras se mantiene el direccionamiento metabólico de doble función.'
            )}
          </p>

          {/* Pipeline Image */}
          <div className="mb-12 flex justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_metasigna-biotech/artifacts/2jf47o8o_y4ZNws72_CG9xH3xxwq9l.png"
              alt={t('Research laboratory with data analysis', 'Laboratorio de investigación con análisis de datos')}
              className="rounded-xl shadow-lg max-w-3xl w-full h-auto"
            />
          </div>

          <div className="space-y-8">
            {candidateClasses.map((candidate, index) => {
              const Icon = candidate.icon;
              return (
                <div key={index} className="bg-gray-50 border border-gray-200 rounded-xl p-8 hover:shadow-lg transition-shadow">
                  <div className="flex items-start space-x-6">
                    <div className={`w-16 h-16 bg-${candidate.color}-100 rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <Icon size={32} className={`text-${candidate.color}-700`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold mb-2 text-[#1F4E78]">{candidate.title}</h3>
                      <p className="text-lg font-semibold text-gray-700 mb-4">{candidate.subtitle}</p>
                      <div className="flex items-center space-x-6 mb-4">
                        <div>
                          <span className="text-sm text-gray-600">{t('Market:', 'Mercado:')}</span>
                          <span className="ml-2 text-lg font-bold text-[#2E5090]">{candidate.market}</span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">{t('Status:', 'Estado:')}</span>
                          <span className="ml-2 text-sm font-medium text-gray-700">{candidate.status}</span>
                        </div>
                      </div>
                      <p className="text-gray-700 leading-relaxed">{candidate.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Platform Scalability */}
          <div className="mt-12 bg-blue-50 border border-blue-200 rounded-xl p-8">
            <h3 className="text-2xl font-bold mb-6 text-[#1F4E78]">
              {t('Platform Scalability & De-Risking', 'Escalabilidad de Plataforma y Reducción de Riesgo')}
            </h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold text-lg mb-3 text-[#2E5090]">
                  {t('Intellectual Property Foundation', 'Base de Propiedad Intelectual')}
                </h4>
                <ul className="space-y-2 text-gray-700">
                  <li>✓ {t('Patent application filed Q1 2025 (OEPM Spain)', 'Solicitud de patente presentada Q1 2025 (OEPM España)')}</li>
                  <li>✓ {t('Coverage: 47 priority conjugate designs across 4 therapeutic classes', 'Cobertura: 47 diseños de conjugados prioritarios en 4 clases terapéuticas')}</li>
                  <li>✓ {t('PCT international filing planned Q1 2025 (18-month confidentiality)', 'Presentación internacional PCT planificada Q1 2025 (18 meses de confidencialidad)')}</li>
                  <li>✓ {t('Freedom to operate: Preliminary FTO analysis shows no blocking patents. Professional clearance ongoing.', 'Libertad para operar: Análisis FTO preliminar no muestra patentes bloqueantes. Verificación profesional en curso.')}</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-3 text-[#2E5090]">
                  {t('Multiple Shots on Goal', 'Múltiples Oportunidades de Éxito')}
                </h4>
                <ul className="space-y-2 text-gray-700">
                  <li>✓ {t('4 independent licensing opportunities', '4 oportunidades de licenciamiento independientes')}</li>
                  <li>✓ {t('Each class addresses different patient populations', 'Cada clase aborda poblaciones de pacientes diferentes')}</li>
                  <li>✓ {t('Failure of one candidate doesn\'t kill platform', 'Fallo de un candidato no mata la plataforma')}</li>
                  <li>✓ {t('Success of any validates approach for others', 'Éxito de cualquiera valida enfoque para otros')}</li>
                </ul>
              </div>
            </div>

            {/* Four Therapeutic Classes Detail */}
            <div className="mt-8 pt-8 border-t border-blue-200">
              <h4 className="font-semibold text-xl mb-6 text-[#1F4E78]">
                {t('The 4 Therapeutic Classes', 'Las 4 Clases Terapéuticas')}
              </h4>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-5 border border-blue-100 shadow-sm">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-8 h-8 bg-[#1F4E78] rounded-full flex items-center justify-center text-white font-bold text-sm">1</div>
                    <h5 className="font-semibold text-[#1F4E78]">
                      {t('Improved Chemotherapeutic Conjugates', 'Conjugados Quimioterapéuticos Mejorados')}
                    </h5>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {t(
                      'Re-patenting improved classic chemotherapies with reduced systemic toxicity and greater tumor selectivity.',
                      'Repatentando quimioclásicos mejorados reduciendo la toxicidad sistémica y mayor selectividad tumoral.'
                    )}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-5 border border-blue-100 shadow-sm">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-8 h-8 bg-[#2E5090] rounded-full flex items-center justify-center text-white font-bold text-sm">2</div>
                    <h5 className="font-semibold text-[#1F4E78]">
                      {t('Targeted Radiotherapeutics', 'Radioterapéuticos Dirigidos')}
                    </h5>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {t(
                      'Metabolic vectors for precision delivery of radioisotopes to tumor cells with minimal off-target effects.',
                      'Vectores metabólicos para entrega de precisión de radioisótopos a células tumorales con mínimos efectos fuera del objetivo.'
                    )}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-5 border border-blue-100 shadow-sm">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-8 h-8 bg-[#FFD700] rounded-full flex items-center justify-center text-[#1F4E78] font-bold text-sm">3</div>
                    <h5 className="font-semibold text-[#1F4E78]">
                      {t('Immunotherapy Enhancers', 'Potenciadores de Inmunoterapia')}
                    </h5>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {t(
                      'Conjugates designed to sensitize tumors to checkpoint inhibitors and overcome immunotherapy resistance.',
                      'Conjugados diseñados para sensibilizar tumores a inhibidores de puntos de control y superar resistencia a inmunoterapia.'
                    )}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-5 border border-blue-100 shadow-sm">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-[#1F4E78] to-[#2E5090] rounded-full flex items-center justify-center text-white font-bold text-sm">4</div>
                    <h5 className="font-semibold text-[#1F4E78]">
                      {t('Diagnostic-Therapeutic (Theranostic)', 'Diagnóstico-Terapéutico (Teranóstico)')}
                    </h5>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {t(
                      'Dual-function compounds for imaging and treatment, enabling personalized therapy selection and response monitoring.',
                      'Compuestos de doble función para imagen y tratamiento, permitiendo selección personalizada de terapia y monitoreo de respuesta.'
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Risk Factors Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-amber-50 border-t border-b border-amber-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={24} className="text-amber-700" />
            </div>
            <div>
              <h3 className="text-xl font-bold mb-3 text-amber-800">
                {t('Investment Risk Factors', 'Factores de Riesgo de Inversión')}
              </h3>
              <ul className="space-y-2 text-amber-900 text-sm">
                <li>• {t('Platform validation: All products are investigational and in preclinical development. Success in preclinical studies does not guarantee clinical success.', 'Validación de plataforma: Todos los productos son investigacionales y en desarrollo preclínico. El éxito en estudios preclínicos no garantiza éxito clínico.')}</li>
                <li>• {t('Regulatory approval: No products are approved for human use. Regulatory pathways are subject to significant uncertainty and may require additional studies.', 'Aprobación regulatoria: Ningún producto está aprobado para uso humano. Las vías regulatorias están sujetas a incertidumbre significativa y pueden requerir estudios adicionales.')}</li>
                <li>• {t('Partnership execution: Licensing and partnership agreements are subject to negotiation and may not be achieved on favorable terms or at all.', 'Ejecución de alianzas: Los acuerdos de licenciamiento y alianzas están sujetos a negociación y pueden no lograrse en términos favorables o en absoluto.')}</li>
                <li>• {t('Funding uncertainty: Achievement of milestones is subject to adequate funding. Additional capital raises may be required.', 'Incertidumbre de financiamiento: El logro de hitos está sujeto a financiamiento adecuado. Pueden requerirse rondas de capital adicionales.')}</li>
                <li>• {t('Timeline and milestones subject to scientific, regulatory, and funding uncertainties. Actual results may differ materially from projections.', 'Cronograma e hitos sujetos a incertidumbres científicas, regulatorias y de financiamiento. Los resultados reales pueden diferir materialmente de las proyecciones.')}</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Investor FAQ Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center text-[#1F4E78]">
            {t('Investor FAQ', 'Preguntas Frecuentes para Inversores')}
          </h2>
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <h4 className="font-semibold text-lg mb-2 text-[#1F4E78]">
                {t('Are your products approved for use?', '¿Están aprobados sus productos para uso?')}
              </h4>
              <p className="text-gray-700">
                {t(
                  'No. All Metasigna conjugates are investigational and in preclinical development. No products are approved for human use. Our technology platform is designed to address significant unmet medical needs, but requires extensive preclinical and clinical validation before any potential regulatory approval.',
                  'No. Todos los conjugados de Metasigna son investigacionales y están en desarrollo preclínico. Ningún producto está aprobado para uso humano. Nuestra plataforma tecnológica está diseñada para abordar necesidades médicas significativas no cubiertas, pero requiere validación preclínica y clínica extensa antes de cualquier potencial aprobación regulatoria.'
                )}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <h4 className="font-semibold text-lg mb-2 text-[#1F4E78]">
                {t('What is the current development stage?', '¿Cuál es la etapa de desarrollo actual?')}
              </h4>
              <p className="text-gray-700">
                {t(
                  'We are in preclinical development with proof-of-concept (POC) studies ongoing. Patent application filed Q1 2025. Our immediate focus is completing POC validation and preparing for PCT international filing.',
                  'Estamos en desarrollo preclínico con estudios de prueba de concepto (POC) en curso. Solicitud de patente presentada Q1 2025. Nuestro enfoque inmediato es completar la validación POC y preparar la presentación internacional PCT.'
                )}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <h4 className="font-semibold text-lg mb-2 text-[#1F4E78]">
                {t('What validation exists for the platform concept?', '¿Qué validación existe para el concepto de plataforma?')}
              </h4>
              <p className="text-gray-700">
                {t(
                  'Our platform is inspired by FDG-PET imaging, which has demonstrated metabolic transporter targeting in humans for 50+ years (40M+ annual procedures). This validates the core principle. However, therapeutic application of this principle requires independent validation through our ongoing preclinical studies.',
                  'Nuestra plataforma está inspirada en la imagen FDG-PET, que ha demostrado direccionamiento de transportadores metabólicos en humanos durante más de 50 años (más de 40M de procedimientos anuales). Esto valida el principio central. Sin embargo, la aplicación terapéutica de este principio requiere validación independiente a través de nuestros estudios preclínicos en curso.'
                )}
              </p>
            </div>
          </div>
          <div className="mt-8 text-center">
            <a href="/warcliv" className="inline-flex items-center space-x-2 text-[#2E5090] hover:text-[#1F4E78] font-semibold">
              <span>{t('Learn more about the scientific foundation', 'Conoce más sobre la fundamentación científica')}</span>
              <ArrowRight size={20} />
            </a>
          </div>
        </div>
      </section>

      {/* Investor Contact Form Section */}
      <section id="investor-form" className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1F4E78] via-[#2E5090] to-[#1F4E78]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('Request Investor Materials', 'Solicitar Materiales de Inversión')}
            </h2>
            <p className="text-lg text-gray-200 max-w-2xl mx-auto">
              {t(
                'We selectively engage with aligned strategic investors.',
                'Nos relacionamos selectivamente con inversores estratégicos alineados.'
              )}
            </p>
          </div>

          {/* Investor Qualification Filter */}
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 mb-8">
            <div className="flex items-start space-x-3">
              <Shield className="text-[#FFD700] flex-shrink-0 mt-1" size={24} />
              <div>
                <h4 className="text-white font-semibold mb-2">
                  {t('Confidential Materials Policy', 'Política de Materiales Confidenciales')}
                </h4>
                <p className="text-gray-300 text-sm leading-relaxed">
                  {t(
                    'Investor materials are available upon request from qualified strategic investors. Early documentation (Executive Summary, Seed Deck) will be released after initial contact. Comprehensive data room access is granted only to selected investors under confidentiality review.',
                    'Los materiales para inversores están disponibles bajo solicitud de inversores estratégicos cualificados. La documentación inicial (Resumen Ejecutivo, Deck Seed) se entregará tras contacto preliminar. El acceso completo a la data room se facilitará únicamente a inversores seleccionados previa revisión y confidencialidad.'
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-10">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name & Email Row */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User size={16} className="inline mr-2 text-[#1F4E78]" />
                    {t('Full Name', 'Nombre Completo')} *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F4E78] focus:border-transparent transition-all"
                    placeholder={t('John Smith', 'Juan García')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail size={16} className="inline mr-2 text-[#1F4E78]" />
                    {t('Email', 'Correo Electrónico')} *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F4E78] focus:border-transparent transition-all"
                    placeholder="email@example.com"
                  />
                </div>
              </div>

              {/* Company & Investment Range Row */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Building size={16} className="inline mr-2 text-[#1F4E78]" />
                    {t('Company / Organization', 'Empresa / Organización')}
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F4E78] focus:border-transparent transition-all"
                    placeholder={t('Company name (optional)', 'Nombre de empresa (opcional)')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <DollarSign size={16} className="inline mr-2 text-[#1F4E78]" />
                    {t('Investment Range', 'Rango de Inversión')}
                  </label>
                  <select
                    name="investmentRange"
                    value={formData.investmentRange}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F4E78] focus:border-transparent transition-all bg-white"
                  >
                    {investmentRanges.map(range => (
                      <option key={range.value} value={range.value}>{range.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('Message / Questions', 'Mensaje / Preguntas')} *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F4E78] focus:border-transparent transition-all resize-none"
                  placeholder={t(
                    'Tell us about your investment interests, questions about our platform, or any specific information you would like to receive...',
                    'Cuéntenos sobre sus intereses de inversión, preguntas sobre nuestra plataforma, o cualquier información específica que desee recibir...'
                  )}
                />
              </div>

              {/* Captcha */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  <ShieldCheck size={16} className="inline mr-2 text-[#1F4E78]" />
                  {t('Security Verification', 'Verificación de Seguridad')} *
                </label>
                <div className="flex items-center space-x-4">
                  <div className="bg-white px-4 py-2 rounded-lg border border-gray-300 font-mono text-lg font-semibold text-[#1F4E78]">
                    {captcha.question} = ?
                  </div>
                  <input
                    type="number"
                    value={captchaInput}
                    onChange={(e) => setCaptchaInput(e.target.value)}
                    required
                    className={`w-24 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#1F4E78] focus:border-transparent transition-all ${
                      captchaError ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="?"
                  />
                  <button
                    type="button"
                    onClick={refreshCaptcha}
                    className="p-2 text-gray-500 hover:text-[#1F4E78] transition-colors"
                    title={t('New question', 'Nueva pregunta')}
                  >
                    <RefreshCw size={20} />
                  </button>
                </div>
              </div>

              {/* GDPR Consent */}
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  name="gdprConsent"
                  checked={formData.gdprConsent}
                  onChange={handleChange}
                  required
                  className="mt-1 h-5 w-5 text-[#1F4E78] border-gray-300 rounded focus:ring-[#1F4E78]"
                />
                <label className="text-sm text-gray-600">
                  {t(
                    'I consent to the processing of my personal data in accordance with the Privacy Policy. I understand that my information will be used solely to respond to my inquiry and will not be shared with third parties without my consent.',
                    'Consiento el tratamiento de mis datos personales de acuerdo con la Política de Privacidad. Entiendo que mi información se utilizará únicamente para responder a mi consulta y no se compartirá con terceros sin mi consentimiento.'
                  )} *
                </label>
              </div>

              {/* Status Messages */}
              {status.message && (
                <div className={`p-4 rounded-lg flex items-center space-x-3 ${
                  status.type === 'success' 
                    ? 'bg-green-50 text-green-800 border border-green-200' 
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {status.type === 'success' ? (
                    <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
                  ) : (
                    <AlertTriangle size={20} className="text-red-600 flex-shrink-0" />
                  )}
                  <span>{status.message}</span>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting || !formData.gdprConsent}
                className="w-full py-4 bg-gradient-to-r from-[#1F4E78] to-[#2E5090] text-white rounded-lg font-semibold text-lg hover:from-[#2E5090] hover:to-[#1F4E78] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <RefreshCw size={20} className="animate-spin" />
                    <span>{t('Sending...', 'Enviando...')}</span>
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    <span>{t('Send Message', 'Enviar Mensaje')}</span>
                  </>
                )}
              </button>

              {/* Direct Contact */}
              <p className="text-center text-sm text-gray-500">
                {t('Or contact us directly at', 'O contáctenos directamente en')}{' '}
                <a href="mailto:investors@metasigna.company" className="text-[#1F4E78] font-semibold hover:underline">
                  investors@metasigna.company
                </a>
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* NEW: Add imported sections */}
      <div id="leadership">
        <TeamSection />
      </div>
      <InvestmentSection />
      <FAQSection />
    </div>
  );
};

export default Investors;
